/**
 * Stock route registration — composition shell wires URL params/search to
 * StockRoute. Restores tax / optional area on Dashboard and area crumbs (AD-9).
 */
import { createRoute, useNavigate } from '@tanstack/react-router';
import { lazy, Suspense, useCallback, type JSX } from 'react';
import { z } from 'zod';

import {
  RequestedTickerSchema,
  StockSearchParamsSchema,
  type AreaId,
  type SectorId,
  type StockSearchParams,
  type Ticker,
  type WindowKey,
} from '@meridian/contracts';

import { Breadcrumbs } from '@/components/chrome/breadcrumbs';
import { Skeleton } from '@/components/primitives/skeleton';
import { chartStudyHrefs } from '@/lib/nav/chart-studies';
import { hrefForSearch } from '@/lib/nav/search';
import {
  areaBackTarget,
  areaTarget,
  dashboardTarget,
  sectorTarget,
  stockTarget,
} from '@/lib/nav/targets';
import { rootRoute } from '@/router-root';

import { InvalidTickerParamError } from './invalid-ticker-param-error';
import { TickerRouteError } from './ticker-route-error';

const loadStockRoute = () => import('./stock-route');

const StockScreen = lazy(async () => {
  const module = await loadStockRoute();
  return { default: module.StockRoute };
});

const paramsSchema = z.object({
  ticker: RequestedTickerSchema,
});

/** Sector hop — preserves win/idx from the stock screen. */
export function sectorDetailTarget(
  sectorId: SectorId,
  search: Pick<StockSearchParams, 'win' | 'idx' | 'book' | 'cmp'>,
): ReturnType<typeof sectorTarget> {
  return sectorTarget(sectorId, search);
}

/** Peer / diversifier ticker hop — tax only when industry. */
export function stockDetailTarget(
  ticker: Ticker,
  search: Pick<StockSearchParams, 'win' | 'idx' | 'tax' | 'area' | 'book' | 'cmp'>,
): ReturnType<typeof stockTarget> {
  return stockTarget(ticker, search);
}

/** Dashboard crumb — omitted/`sector` tax is stripped (compatibility form). */
export function dashboardBackTarget(
  search: Pick<StockSearchParams, 'win' | 'idx' | 'tax' | 'book' | 'cmp'>,
): ReturnType<typeof dashboardTarget> {
  return dashboardTarget(search);
}

/**
 * Industry heading hop — subject's serving area, not the URL crumb.
 *
 * Deliberately drops the active index filter and navigates under `ALL`.
 * `/v1/securities/:ticker/peers` is index-INDEPENDENT (its `SeriesQuery` carries
 * only a window, and the live route reads the universe under `ALL`), while
 * `/area/$areaId` is index-SCOPED and both adapters 404 an area whose filtered
 * member set is empty. Forwarding `idx` therefore navigated to an area under a
 * filter the panel was never computed against: measured over the local
 * universe, 52/129 subjects dead-ended under `idx=NDX` and 39/129 under
 * `idx=DJI`. `ALL` is the only filter under which the linked area is guaranteed
 * to contain the rows the panel just displayed.
 */
export function areaDetailTarget(
  areaId: AreaId,
  search: Pick<StockSearchParams, 'win' | 'idx' | 'book' | 'cmp'>,
): ReturnType<typeof areaTarget> {
  return areaTarget(areaId, { win: search.win, idx: 'ALL', book: search.book, cmp: search.cmp });
}

export { areaBackTarget };

function stockCrumbs(ticker: Ticker, search: StockSearchParams) {
  const dash = dashboardBackTarget(search);
  const area = areaBackTarget(search);
  const industry =
    area === undefined
      ? []
      : [
          {
            label: 'Industry',
            href: hrefForSearch(`/area/${encodeURIComponent(area.params.areaId)}`, area.search),
          },
        ];
  return [
    { label: 'Dashboard', href: hrefForSearch('/', dash.search) },
    ...industry,
    { label: ticker },
  ];
}

function useStockDetailNavigation(search: StockSearchParams) {
  const navigate = useNavigate({ from: stockRoute.fullPath });

  const onRangeChange = useCallback(
    (range: WindowKey) => {
      void navigate({
        search: (prev) => ({ ...prev, range }),
        replace: true,
      });
    },
    [navigate],
  );

  const onTickerSelect = useCallback(
    (next: Ticker) => {
      void navigate(stockDetailTarget(next, search));
    },
    [navigate, search],
  );

  const onSectorSelect = useCallback(
    (sc: SectorId) => {
      void navigate(
        sectorDetailTarget(sc, {
          win: search.win,
          idx: search.idx,
          book: search.book,
          cmp: search.cmp,
        }),
      );
    },
    [navigate, search.win, search.idx, search.book, search.cmp],
  );

  const onAreaSelect = useCallback(
    (areaId: AreaId) => {
      void navigate(
        areaDetailTarget(areaId, {
          win: search.win,
          idx: search.idx,
          book: search.book,
          cmp: search.cmp,
        }),
      );
    },
    [navigate, search.win, search.idx, search.book, search.cmp],
  );

  return { onRangeChange, onTickerSelect, onSectorSelect, onAreaSelect };
}

function StockRouteAdapter(): JSX.Element {
  const { ticker } = stockRoute.useParams();
  const search = stockRoute.useSearch();
  const { onRangeChange, onTickerSelect, onSectorSelect, onAreaSelect } =
    useStockDetailNavigation(search);

  return (
    <Suspense fallback={<Skeleton height="18rem" label="Loading stock" />}>
      <Breadcrumbs items={stockCrumbs(ticker, search)} />
      <StockScreen
        ticker={ticker}
        window={search.win}
        range={search.range}
        onRangeChange={onRangeChange}
        onTickerSelect={onTickerSelect}
        onSectorSelect={onSectorSelect}
        onAreaSelect={onAreaSelect}
        studyHrefs={chartStudyHrefs(ticker, search)}
      />
    </Suspense>
  );
}

export const stockRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/stock/$ticker',
  params: {
    parse: (raw) => {
      const result = paramsSchema.safeParse({ ticker: raw.ticker });
      if (!result.success) {
        /* Carries the sentinel the error boundary discriminates on. */
        throw new InvalidTickerParamError();
      }
      return result.data;
    },
    stringify: (p) => ({ ticker: p.ticker }),
  },
  validateSearch: (search: Record<string, unknown>) => StockSearchParamsSchema.parse(search),
  loader: loadStockRoute,
  component: StockRouteAdapter,
  errorComponent: TickerRouteError,
});
